import { Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import { ArrowUpRightFromSquareIcon } from '@c2fo/liquidity/icons';
import Skeleton from '@/enterprise/components/Skeleton';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import RecurringRulesForm, {
  RecurringRuleFormValues,
  RecurringRulesFormProps,
} from '@/enterprise/features/recurringRules/RecurringRulesForm';
import defaultRuleValues from '@/enterprise/features/recurringRules/utils/defaultRuleValues';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import calendarSvg from '@/shared/assets/calendar.svg?url';
import documentCheckSvg from '@/shared/assets/documentCheck.svg?url';
import invoicesSvg from '@/shared/assets/invoices.svg?url';
import levelsSvg from '@/shared/assets/levels.svg?url';
import lightbulbSvg from '@/shared/assets/lightbulb.svg?url';
import scalesSvg from '@/shared/assets/scales.svg?url';
import transferringDataSvg from '@/shared/assets/transferringdata.svg?url';
import { ButtonLink } from '@/shared/components/ButtonLink';
import Divider from '@/shared/components/Divider';
import NavigateWithRedirect from '@/shared/components/NavigateWithRedirect';
import useSrm from '@/shared/data/useSrm';
import useUserDetails from '@/shared/data/useUserDetails';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useIsAccountQualifiedToNotifySrm from '@/utils/useIsAccountQualifiedToNotifySrm';
import useRestrictions from '@/utils/useRestrictions';
import useSubmitInformationRequestInvoiceRules from '../data/useSubmitInformationRequestInvoiceRules';
import AutomationTable from '../features/automation/AutomationTable';
import SuggestedTemplateCard from '../features/automation/components/SuggestedTemplateCard';
import RequestInformationModal from '../features/recurringRules/components/RequestInformationModal';

const AutomationManagerComponent = () => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { data: takerMarkets = [] } = useTakerMarkets();
  const [recurringRulesFormOpen, setRecurringRulesFormOpen] = useState<boolean>(false);
  const [ruleModalProps, setRuleModalProps] = useState<Pick<RecurringRulesFormProps, 'defaultValues' | 'mode'> | null>(
    null
  );
  const { getRestrictions } = useRestrictions();
  const { canEditRecurringRules } = getRestrictions([]);
  const [createAutomationModalOpen, setCreateAutomationModalOpen] = useState(false);
  const isAccountQualifiedToNotifySrm = useIsAccountQualifiedToNotifySrm();
  const [invoiceRulesInfoRequestSubmitted, setInvoiceRulesInfoRequestSubmitted] = useState(false);
  const {
    mutateAsync: submitInformationRequestInvoiceRules,
    isPending: isLoadingSubmitInformationRequestInvoiceRules,
  } = useSubmitInformationRequestInvoiceRules();
  const showSnackbar = useSnackbar();
  const srm = useSrm();
  const { data: userData } = useUserDetails();
  const [showRequestInformationModal, setShowRequestInformationModal] = useState(false);
  const enableUnification = useEnableUnification();

  const showRecurringRulesModal = (defaultValues?: Partial<RecurringRuleFormValues>) => {
    setRuleModalProps({ defaultValues });
    setRecurringRulesFormOpen(true);
  };

  const onNewRuleClick = () => {
    setCreateAutomationModalOpen(false);
    showRecurringRulesModal();
    trackEnterpriseEvent('recurring-rules::opened', {
      referrer: 'rules-manager',
      source: 'cta',
    });
  };

  const closeRecurringRulesModal = () => {
    setRecurringRulesFormOpen(false);
    trackEnterpriseEvent('recurring-rules::closed');
  };

  const sendInformationRequest = async () => {
    try {
      // Utilizing existing event here instead of rebranding to "automation" for continuity
      trackEnterpriseEvent('recurring-rules::information-request::clicked', {
        srmEmail: srm.emailAddress,
        accountId: userData?.me?.account?.id,
        userEmail: userData?.me?.emailAddress,
      });
      await submitInformationRequestInvoiceRules();
      setShowRequestInformationModal(true);
    } catch {
      showSnackbar({
        message: t('core.error'),
      });
    } finally {
      setInvoiceRulesInfoRequestSubmitted(true);
    }
  };

  const offerScheduleConfig = enableUnification
    ? {
        description: t('automation.buildOfferScheduleDescription'),
        buttonLink: asEnterpriseLink('build-offer'),
        buttonText: t('core.buildOffer'),
      }
    : {
        description: t('automation.offerScheduleDescription'),
        buttonLink: asEnterpriseLink('markets'),
        buttonText: t('core.marketsPage'),
      };

  return (
    <>
      <RecurringRulesForm
        onClose={closeRecurringRulesModal}
        open={recurringRulesFormOpen}
        referrer="earlyPayAutomation"
        takerMarkets={takerMarkets}
        {...ruleModalProps}
      />
      <RequestInformationModal
        open={showRequestInformationModal}
        onClose={() => setShowRequestInformationModal(false)}
      />
      <Modal onClose={() => setCreateAutomationModalOpen(false)} open={createAutomationModalOpen} size="sm">
        <ModalTitleInverse>{t('automation.createAutomation')}</ModalTitleInverse>
        <ModalContent className="space-y-3">
          <p>{t('automation.createAutomationDescription')}</p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col justify-between rounded-md border bg-white p-4 shadow-md">
              <div>
                <div className="flex items-center justify-between gap-8">
                  <p className="w-full text-lg font-bold">{t('automation.invoiceRules')}</p>
                  <img src={levelsSvg} className="h-[75px] w-[65px] shrink-0 grow-0" alt="" />
                </div>
                <p className="text-sm text-text-secondary">{t('automation.invoiceAutomationDescription')}</p>
              </div>
              <Button
                className="mt-6 w-full"
                size="md"
                variant="primary"
                disabled={!canEditRecurringRules}
                onClick={onNewRuleClick}
              >
                {t('core.create')}
              </Button>
            </div>

            <div className="flex flex-col justify-between rounded-md border bg-white p-4 shadow-md">
              <div>
                <div className="flex items-center justify-between gap-8">
                  <p className="w-full text-lg font-bold">{t('automation.offerSchedule')}</p>
                  <img src={calendarSvg} className="h-[75px] w-[65px] shrink-0 grow-0" alt="" />
                </div>
                <p className="text-sm text-text-secondary">{offerScheduleConfig.description}</p>
              </div>
              <ButtonLink
                className="mt-6 w-full"
                size="md"
                variant="primary"
                to={offerScheduleConfig.buttonLink}
                {...(!enableUnification && { icon: <ArrowUpRightFromSquareIcon fill="white" /> })}
              >
                {offerScheduleConfig.buttonText}
              </ButtonLink>
            </div>
          </div>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setCreateAutomationModalOpen(false)} variant="secondary">
            {t('core.close')}
          </Button>
        </ModalActions>
      </Modal>

      <div className="flex flex-col gap-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="m-0 pb-4 text-4xl font-medium sm:pb-0">{t('core.earlyPayAutomation')}</h1>
          <IntercomDataTarget target="automationCTA">
            <Button className="w-full sm:w-fit" onClick={() => setCreateAutomationModalOpen(true)} variant="cta">
              {t('automation.createAutomation')}
            </Button>
          </IntercomDataTarget>
        </div>

        <div className="relative flex flex-col items-center justify-between gap-8 rounded-lg bg-white p-8 shadow-md md:flex-row">
          <div className="w-full space-y-4">
            <h2 className="m-0 p-0 pb-6 font-sans text-3xl font-bold">{t('automation.earlyPayMadeEasy')}</h2>

            <div className="space-y-4">
              <p>{t('automation.revolutionizeDescription')}</p>

              <p>
                <span className="italic">{t('automation.setAndForget')}</span>:{' '}
                {t('automation.setAndForgetDescription')}
              </p>

              <p>{t('automation.startingDescription')}</p>
            </div>

            {isAccountQualifiedToNotifySrm && (
              <>
                <Divider />
                <div className="flex flex-col items-center gap-4 md:flex-row">
                  <p className="w-full text-text-secondary">
                    <Trans
                      i18nKey="automation.srmNotificationDescription"
                      components={{
                        style: <span className="italic" />,
                        helpArticleLink: (
                          <a
                            className="font-medium text-blue-500"
                            href="http://help.c2fo.com/en/articles/11055450-early-pay-automation-101"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {/* NOTE: This text is a placeholder and will be swapped out with value in json file tag */}
                            help article
                          </a>
                        ),
                      }}
                    />
                  </p>

                  <Button
                    variant="tertiary"
                    onClick={sendInformationRequest}
                    disabled={isLoadingSubmitInformationRequestInvoiceRules || invoiceRulesInfoRequestSubmitted}
                    loading={isLoadingSubmitInformationRequestInvoiceRules}
                  >
                    {t('recurringRules.noActiveRulesCtaBeta')}
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="flex h-full shrink-0 grow-0 items-center justify-center">
            <img src={transferringDataSvg} alt="transferring data graphic" className="w-[320px]" />
          </div>
        </div>

        <IntercomDataTarget target="suggestedAutomations">
          <h2 className="m-0 pb-4 text-2xl">{t('automation.suggestedTemplates')}</h2>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
            <SuggestedTemplateCard
              title={t('automation.templates.quarterEnd.title')}
              description={t('automation.templates.quarterEnd.description')}
              svgImgSrc={invoicesSvg}
              chipLabel={t('automation.templates.quarterEnd.chipLabel')}
              chipColorVariant="purple"
              onClick={() => {
                showRecurringRulesModal({
                  invoiceRules: [
                    {
                      category: 'dueDate',
                      criteria: [{ type: 'fromDueDate', value: defaultRuleValues.dueDate.value as string }],
                    },
                  ],
                });
              }}
              buttonLabel={t('automation.templates.view')}
            />
            <SuggestedTemplateCard
              title={t('automation.templates.invoiceIds.title')}
              description={t('automation.templates.invoiceIds.description')}
              svgImgSrc={documentCheckSvg}
              chipLabel={t('automation.templates.invoiceIds.chipLabel')}
              chipColorVariant="pink"
              onClick={() => {
                showRecurringRulesModal({
                  invoiceRules: [{ category: 'invoiceId', criteria: [] }],
                });
              }}
              buttonLabel={t('automation.templates.view')}
            />
            <SuggestedTemplateCard
              title={t('automation.templates.reduceTerms.title')}
              description={t('automation.templates.reduceTerms.description')}
              svgImgSrc={scalesSvg}
              chipLabel={t('automation.templates.reduceTerms.chipLabel')}
              chipColorVariant="orange"
              onClick={() => {
                showRecurringRulesModal({
                  invoiceRules: [{ category: 'dpe', criteria: [{ type: 'lteDpe', value: '30' }] }],
                });
              }}
              buttonLabel={t('automation.templates.view')}
            />
            <SuggestedTemplateCard
              title={t('automation.templates.custom.title')}
              description={t('automation.templates.custom.description')}
              svgImgSrc={lightbulbSvg}
              chipLabel={t('automation.templates.custom.chipLabel')}
              chipColorVariant="green"
              onClick={() => {
                showRecurringRulesModal();
              }}
              buttonLabel={t('automation.templates.createAutomation')}
            />
          </div>
        </IntercomDataTarget>

        <AutomationTable openCreateAutomationModal={() => setCreateAutomationModalOpen(true)} />
      </div>
    </>
  );
};

// TODO: Make this match how the new automation page ends up looking like
const LoadingSkeleton = () => {
  const { t } = useTranslation();

  return (
    <>
      <h1 className="m-0 pb-4 text-4xl font-medium sm:pb-0">{t('core.earlyPayAutomation')}</h1>
      <div className="mt-16 space-y-8">
        <Skeleton height="h-80" />
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
          <Skeleton height="h-52" />
          <Skeleton height="h-52" />
          <Skeleton height="h-52" />
          <Skeleton height="h-52" />
        </div>
        <Skeleton height="h-80" />
      </div>
    </>
  );
};

const Automation = () => {
  const enableAutomations = useEnableAutomations();

  if (!enableAutomations) {
    return <NavigateWithRedirect to={asEnterpriseLink('homepage')} />;
  }

  return (
    <Suspense fallback={<LoadingSkeleton />}>
      <AutomationManagerComponent />
    </Suspense>
  );
};

export default Automation;
